(() => {
  'use strict';

  const params = new URLSearchParams(window.location.search);
  if (params.get('ag_debug') !== '1') return;
  if (window.__hanezzaProductRatingDebugLoaded) return;
  window.__hanezzaProductRatingDebugLoaded = true;

  const errors = [];
  const cspViolations = [];
  const startedAt = new Date().toISOString();
  const ratingSelector = 'a[href="#testimonials"],a[href="#product-reviews"],a[href="#reviews"],a[href$="#testimonials"],a[href$="#product-reviews"],a[href$="#reviews"]';
  let panel = null;
  let output = null;

  function compactText(value, limit = 180) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, limit);
  }

  function isVisible(element) {
    if (!element || !element.isConnected) return false;
    let current = element;
    while (current && current !== document.documentElement) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      current = current.parentElement;
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function cssPath(element) {
    if (!element || element.nodeType !== 1) return null;
    const parts = [];
    let current = element;
    while (current && current !== document.documentElement && parts.length < 8) {
      let part = current.tagName.toLowerCase();
      if (current.id) {
        part += `#${current.id}`;
        parts.unshift(part);
        break;
      }
      const classes = Array.from(current.classList || []).slice(0, 3);
      if (classes.length) part += `.${classes.join('.')}`;
      parts.unshift(part);
      current = current.parentElement;
    }
    return parts.join(' > ');
  }

  function describeElement(element, root) {
    if (!element) return null;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return {
      path: cssPath(element),
      tag: element.tagName,
      id: element.id || null,
      className: compactText(element.className, 220),
      href: element.getAttribute?.('href') || null,
      text: compactText(element.textContent),
      svgCount: element.querySelectorAll?.('svg').length || 0,
      visible: isVisible(element),
      insideRoot: Boolean(root && root.contains(element)),
      insideMobile: Boolean(element.closest?.('.custom-mobile-view')),
      insideDesktop: Boolean(element.closest?.('.custom-desktop-view, #product-details-container')),
      display: style.display,
      visibility: style.visibility,
      opacity: style.opacity,
      rect: {
        x: Math.round(rect.x),
        y: Math.round(rect.y),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      }
    };
  }

  function collectRatingCandidates(scope, root) {
    if (!scope) return [];
    return Array.from(scope.querySelectorAll('a, div, span'))
      .filter((element) => {
        const text = (element.textContent || '').toLocaleLowerCase('tr-TR');
        return element.querySelectorAll('svg').length >= 4 && /review|yorum|değerlendirme/.test(text);
      })
      .slice(0, 40)
      .map((element) => describeElement(element, root));
  }

  function collectScripts() {
    return Array.from(document.scripts).map((script) => ({
      src: script.src || '[inline]',
      async: script.async,
      defer: script.defer,
      type: script.type || 'text/javascript'
    })).filter((item) => /hanezza|product|review|air|theme|vendor/i.test(item.src)).slice(-80);
  }

  function collectResources() {
    if (!window.performance?.getEntriesByType) return [];
    return performance.getEntriesByType('resource')
      .filter((entry) => /hanezza|product|review|air|theme|vendor/i.test(entry.name))
      .slice(-100)
      .map((entry) => ({
        name: entry.name,
        initiatorType: entry.initiatorType,
        durationMs: Math.round(entry.duration),
        transferSize: entry.transferSize || 0,
        decodedBodySize: entry.decodedBodySize || 0
      }));
  }

  function collect() {
    const root = document.querySelector('#hanezza-product-scope');
    const status = window.HanezzaProductRatingPosition?.status?.() || null;
    const documentHeadings = Array.from(document.querySelectorAll('h1')).map((element) => describeElement(element, root));
    const rootHeadings = root ? Array.from(root.querySelectorAll('h1')).map((element) => describeElement(element, root)) : [];
    const documentRatingLinks = Array.from(document.querySelectorAll(ratingSelector)).map((element) => describeElement(element, root));
    const rootRatingLinks = root ? Array.from(root.querySelectorAll(ratingSelector)).map((element) => describeElement(element, root)) : [];
    const scripts = collectScripts();
    const ratingScriptLoads = scripts.filter((item) => /hanezza-product-rating-position\.js/.test(item.src));

    return {
      generatedAt: new Date().toISOString(),
      startedAt,
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        desktop1024: window.matchMedia('(min-width: 1024px)').matches,
        desktop768: window.matchMedia('(min-width: 768px)').matches
      },
      diagnosis: {
        activeMode: window.matchMedia('(min-width: 1024px)').matches ? 'desktop' : 'mobile',
        rootFound: Boolean(root),
        rootHasMobileView: Boolean(root?.querySelector('.custom-mobile-view')),
        rootHasDesktopView: Boolean(root?.querySelector('.custom-desktop-view, #product-details-container')),
        visibleDocumentHeadings: documentHeadings.filter((item) => item.visible).length,
        visibleRootHeadings: rootHeadings.filter((item) => item.visible).length,
        visibleDocumentRatingLinks: documentRatingLinks.filter((item) => item.visible).length,
        visibleRootRatingLinks: rootRatingLinks.filter((item) => item.visible).length,
        ratingScriptLoadCount: ratingScriptLoads.length,
        ratingScriptSources: ratingScriptLoads.map((item) => item.src)
      },
      document: {
        readyState: document.readyState,
        bodyClass: document.body?.className || null,
        rootFound: Boolean(root),
        rootDataset: root ? { ...root.dataset } : null
      },
      positionStatus: status,
      documentHeadings,
      rootHeadings,
      documentRatingLinks,
      rootRatingLinks,
      documentRatingCandidates: collectRatingCandidates(document, root),
      rootRatingCandidates: collectRatingCandidates(root, root),
      scripts,
      resources: collectResources(),
      cspMeta: Array.from(document.querySelectorAll('meta[http-equiv="Content-Security-Policy"]')).map((meta) => meta.content),
      cspViolations: cspViolations.slice(-20),
      errors: errors.slice(-30)
    };
  }

  function render() {
    if (!output) return;
    output.textContent = JSON.stringify(collect(), null, 2);
  }

  function createButton(label, handler) {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.style.cssText = 'border:1px solid #4b5563;background:#111827;color:#fff;border-radius:6px;padding:6px 10px;cursor:pointer;font:600 12px/1.2 system-ui';
    button.addEventListener('click', handler);
    return button;
  }

  function mount() {
    if (panel) return;

    panel = document.createElement('section');
    panel.id = 'hanezza-rating-debug-overlay';
    panel.style.cssText = [
      'position:fixed',
      'left:0',
      'right:0',
      'top:0',
      'z-index:2147483647',
      'max-height:52vh',
      'display:flex',
      'flex-direction:column',
      'background:rgba(3,7,18,.97)',
      'color:#e5e7eb',
      'border-bottom:3px solid #C89436',
      'box-shadow:0 10px 30px rgba(0,0,0,.45)',
      'font:12px/1.45 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace'
    ].join(';');

    const header = document.createElement('div');
    header.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 10px;background:#111827;position:sticky;top:0';

    const title = document.createElement('strong');
    title.textContent = 'Hanezza Ürün Puanı Tanı Paneli · ag_debug=1';
    title.style.cssText = 'margin-right:auto;color:#fbbf24;font:700 13px/1.2 system-ui';

    header.append(
      title,
      createButton('Düzeltmeyi Çalıştır', () => {
        window.HanezzaProductRatingPosition?.apply?.();
        window.setTimeout(render, 80);
      }),
      createButton('Yenile', render),
      createButton('JSON Kopyala', async () => {
        const text = JSON.stringify(collect(), null, 2);
        try {
          await navigator.clipboard.writeText(text);
        } catch (_) {
          window.prompt('Tanı JSON verisini kopyalayın:', text);
        }
      }),
      createButton('Kapat', () => panel.remove())
    );

    output = document.createElement('pre');
    output.style.cssText = 'margin:0;padding:10px;overflow:auto;white-space:pre-wrap;word-break:break-word;color:#d1d5db';

    panel.append(header, output);
    document.documentElement.appendChild(panel);
    render();

    [250, 1000, 3000].forEach((delay) => window.setTimeout(render, delay));
  }

  window.addEventListener('error', (event) => {
    errors.push({
      type: 'error',
      message: event.message,
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
      stack: event.error?.stack || null,
      at: new Date().toISOString()
    });
    render();
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    errors.push({
      type: 'unhandledrejection',
      reason: String(event.reason),
      stack: event.reason?.stack || null,
      at: new Date().toISOString()
    });
    render();
  });

  document.addEventListener('securitypolicyviolation', (event) => {
    cspViolations.push({
      blockedURI: event.blockedURI,
      violatedDirective: event.violatedDirective,
      effectiveDirective: event.effectiveDirective,
      sourceFile: event.sourceFile,
      line: event.lineNumber,
      at: new Date().toISOString()
    });
    render();
  });

  window.HanezzaProductRatingDebug = { collect, render, errors, cspViolations };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
})();
